import { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import type { UserRowV2 } from "./sql";
import {
  buildHistograms, STAGES, HOURLY_MAX_DAYS, FIFTEEN_MIN_MAX_DAYS,
} from "./helpers";
import type { Granularity, StageKey } from "./helpers";
import { applyCohort, computeMeasures, COHORT_COLORS, DEFAULT_DERIVED, newTimingId } from "./cohorts";
import type { CohortDef, StageTiming, DayOp } from "./cohorts";
import type { Filters } from "../../lib/funnel/filters";
import { MultiChip } from "../../lib/funnel/filters";
import { COMPANY_SIZE_BUCKETS } from "../../lib/funnel/filters";
import { SectionCardHeader } from "../../components/ChartCard";
import { fmtN, fmtPct, groupCountry, countryLabel, COUNTRY_GROUPS } from "../../lib/funnel/helpers";
import { PRIMARY, MUTED, FG1 } from "../../lib/funnel/theme";

type Measure = "histograms" | "kpi-table";

const DEFAULT_FILTERS: Filters = {
  months: [],
  countries: [],
  roles: [],
  companySizes: [],
  excludeSupport: true,
  granularity: "month",
  cohortDayFrom: null,
  cohortDayTo: null,
  demoBucketing: "signup_date",
  observationDays: 90,
};

let counter = 0;
const newId = () => `c${++counter}`;

function cloneDerived(d: CohortDef["derived"]): CohortDef["derived"] {
  return {
    ...d,
    stageTimings: d.stageTimings.map((t) => ({ ...t })),
    excludePaidWithinHoursOfSignup: { ...d.excludePaidWithinHoursOfSignup },
  };
}

function makeCohort(label: string, color: string): CohortDef {
  return {
    id: newId(),
    label,
    color,
    filters: { ...DEFAULT_FILTERS },
    derived: cloneDerived(DEFAULT_DERIVED),
  };
}

export function CohortLab({
  rows,
  availableCountries,
  availableRoles,
}: {
  rows: UserRowV2[];
  availableCountries: string[];
  availableRoles: string[];
}) {
  const [cohorts, setCohorts] = useState<CohortDef[]>(() => [
    {
      ...makeCohort("Calendar within 24h", COHORT_COLORS[0]!),
      derived: {
        ...cloneDerived(DEFAULT_DERIVED),
        stageTimings: [{ id: newTimingId(), stage: "calendar", op: "within", days: 1 }],
      },
    },
    {
      ...makeCohort("Calendar after 24h", COHORT_COLORS[1]!),
      derived: {
        ...cloneDerived(DEFAULT_DERIVED),
        stageTimings: [{ id: newTimingId(), stage: "calendar", op: "after", days: 1 }],
      },
    },
  ]);
  const [measure, setMeasure] = useState<Measure>("histograms");
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [observationDays, setObservationDays] = useState<number>(90);
  const [selectedStages, setSelectedStages] = useState<StageKey[]>(() => STAGES.map((s) => s.key));

  const effectiveGranularity: Granularity =
    granularity === "15min" && observationDays > FIFTEEN_MIN_MAX_DAYS
      ? (observationDays <= HOURLY_MAX_DAYS ? "hour" : "day")
      : granularity === "hour" && observationDays > HOURLY_MAX_DAYS
        ? "day"
        : granularity;

  const updateCohort = (id: string, next: Partial<CohortDef>) => {
    setCohorts((cs) => cs.map((c) => (c.id === id ? { ...c, ...next } : c)));
  };
  const removeCohort = (id: string) => setCohorts((cs) => cs.filter((c) => c.id !== id));
  const duplicateCohort = (id: string) => {
    setCohorts((cs) => {
      const src = cs.find((c) => c.id === id);
      if (!src) return cs;
      const colorIdx = cs.length % COHORT_COLORS.length;
      const copy: CohortDef = {
        ...src,
        id: newId(),
        label: `${src.label} (copy)`,
        color: COHORT_COLORS[colorIdx]!,
        filters: { ...src.filters },
        derived: cloneDerived(src.derived),
      };
      return [...cs, copy];
    });
  };
  const addCohort = () => {
    setCohorts((cs) => {
      const colorIdx = cs.length % COHORT_COLORS.length;
      return [...cs, makeCohort(`Cohort ${cs.length + 1}`, COHORT_COLORS[colorIdx]!)];
    });
  };

  const cohortUsers = useMemo(
    () => cohorts.map((c) => ({ cohort: c, users: applyCohort(rows, c) })),
    [cohorts, rows],
  );

  const stageOptions = STAGES.map((s) => ({ value: s.key, label: s.label }));

  return (
    <section className="card" style={{ borderColor: "rgba(47, 122, 138, 0.35)" }}>
      <SectionCardHeader
        title="Cohort lab"
        subtitle={
          <>
            Compare arbitrary cohorts side-by-side on the same query. Each cohort is independently
            filtered (including v2-specific filters like calendar-connected timing, demo path, and the
            paid-within-Nh exclusion). Pick a measure on the right to render the comparison.
          </>
        }
        headerExtra={
          <div role="group" aria-label="Measure" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
            {([
              { key: "histograms" as const, label: "Time-to-stage histograms" },
              { key: "kpi-table" as const, label: "KPI comparison table" },
            ]).map((m) => {
              const active = measure === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMeasure(m.key)}
                  style={{
                    padding: "3px 10px",
                    background: active ? PRIMARY : "transparent",
                    color: active ? "#fff" : FG1,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        }
      />
      <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {cohortUsers.map(({ cohort, users }) => (
            <CohortCard
              key={cohort.id}
              cohort={cohort}
              userCount={users.length}
              availableCountries={availableCountries}
              availableRoles={availableRoles}
              onChange={(next) => updateCohort(cohort.id, next)}
              onDuplicate={() => duplicateCohort(cohort.id)}
              onRemove={cohorts.length > 1 ? () => removeCohort(cohort.id) : undefined}
            />
          ))}
          <button
            type="button"
            onClick={addCohort}
            style={{
              alignSelf: "flex-start",
              fontSize: 12,
              padding: "5px 10px",
              border: `1px dashed ${MUTED}`,
              borderRadius: 6,
              background: "transparent",
              color: FG1,
              cursor: "pointer",
            }}
          >
            + Add cohort
          </button>
        </div>

        {measure === "histograms" ? (
          <HistogramSection
            cohortUsers={cohortUsers}
            granularity={effectiveGranularity}
            onGranularityChange={setGranularity}
            observationDays={observationDays}
            onObservationDaysChange={setObservationDays}
            selectedStages={selectedStages}
            onSelectedStagesChange={setSelectedStages}
            stageOptions={stageOptions}
          />
        ) : (
          <KpiTableSection cohortUsers={cohortUsers} />
        )}
      </div>
    </section>
  );
}

function CohortCard({
  cohort,
  userCount,
  availableCountries,
  availableRoles,
  onChange,
  onDuplicate,
  onRemove,
}: {
  cohort: CohortDef;
  userCount: number;
  availableCountries: string[];
  availableRoles: string[];
  onChange: (next: Partial<CohortDef>) => void;
  onDuplicate: () => void;
  onRemove?: () => void;
}) {
  const setFilters = (patch: Partial<Filters>) =>
    onChange({ filters: { ...cohort.filters, ...patch } });
  const setDerived = (patch: Partial<CohortDef["derived"]>) =>
    onChange({ derived: { ...cohort.derived, ...patch } });

  const presentGroups = new Set(availableCountries);
  const countryOpts = COUNTRY_GROUPS
    .filter((g) => presentGroups.has(g))
    .map((g) => ({ value: g, label: countryLabel(g) }));
  const roleOpts = availableRoles.map((r) => ({ value: r, label: r }));
  const sizeOpts = COMPANY_SIZE_BUCKETS.map((b) => ({ value: b.key, label: b.label }));

  const dayFrom = cohort.filters.cohortDayFrom ?? "";
  const dayTo = cohort.filters.cohortDayTo ?? "";

  return (
    <div
      style={{
        border: `1px solid var(--border)`,
        borderLeft: `4px solid ${cohort.color}`,
        borderRadius: 6,
        padding: "10px 12px",
        background: "var(--bg-2)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <ColorSwatch
          value={cohort.color}
          onChange={(c) => onChange({ color: c })}
        />
        <input
          type="text"
          value={cohort.label}
          onChange={(e) => onChange({ label: e.target.value })}
          style={{
            fontSize: 13,
            fontWeight: 600,
            padding: "2px 6px",
            border: "1px solid transparent",
            borderRadius: 4,
            background: "transparent",
            color: FG1,
            minWidth: 200,
            flex: "1 1 auto",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
        />
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
          {fmtN(userCount)} users
        </span>
        <button
          type="button"
          onClick={onDuplicate}
          title="Duplicate cohort"
          style={iconBtn}
        >
          <i className="ti ti-copy" />
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove cohort"
            style={iconBtn}
          >
            <i className="ti ti-x" />
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <DateRangeChip
            from={dayFrom}
            to={dayTo}
            onChange={(f, t) => setFilters({
              cohortDayFrom: f || null,
              cohortDayTo: t || (f || null),
              granularity: f || t ? "day" : "month",
              months: [],
            })}
            onClear={() => setFilters({ cohortDayFrom: null, cohortDayTo: null, granularity: "month", months: [] })}
          />
          <MultiChip
            label="Country"
            allLabel="All countries"
            pluralNoun="countries"
            options={countryOpts}
            selected={cohort.filters.countries}
            onChange={(next) => setFilters({ countries: next })}
          />
          <MultiChip
            label="Role"
            allLabel="All roles"
            pluralNoun="roles"
            options={roleOpts}
            selected={cohort.filters.roles}
            onChange={(next) => setFilters({ roles: next })}
          />
          <MultiChip
            label="Size"
            allLabel="All sizes"
            pluralNoun="sizes"
            options={sizeOpts}
            selected={cohort.filters.companySizes}
            onChange={(next) => setFilters({ companySizes: next })}
          />
          <TriStateChip
            label="Path"
            value={cohort.derived.hadDemo}
            yesLabel="Demo path"
            noLabel="Self-serve"
            onChange={(v) => setDerived({ hadDemo: v })}
          />
          <TriStateChip
            label="Paid"
            value={cohort.derived.convertedToPaid}
            yesLabel="Converted"
            noLabel="Not converted"
            onChange={(v) => setDerived({ convertedToPaid: v })}
          />
          <ReachedStageChip
            value={cohort.derived.reachedStage}
            onChange={(v) => setDerived({ reachedStage: v })}
          />
          <ExcludePaidChip
            value={cohort.derived.excludePaidWithinHoursOfSignup}
            onChange={(v) => setDerived({ excludePaidWithinHoursOfSignup: v })}
          />
          <label className="filter-switch" title="Exclude support roles" style={{ fontSize: 11 }}>
            <input
              type="checkbox"
              checked={cohort.filters.excludeSupport}
              onChange={(e) => setFilters({ excludeSupport: e.target.checked })}
            />
            <span className="track"><span className="thumb" /></span>
            <span className="label-text">Excl. support</span>
          </label>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--fg-3)", fontWeight: 500 }}>Stage timing:</span>
          <StageTimingsControl
            timings={cohort.derived.stageTimings}
            onChange={(next) => setDerived({ stageTimings: next })}
          />
        </div>
      </div>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: 4,
  padding: "3px 6px",
  cursor: "pointer",
  fontSize: 12,
  color: FG1,
};

function ColorSwatch({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        style={{
          display: "inline-block",
          width: 16, height: 16, borderRadius: 4,
          background: value,
          border: "1px solid rgba(0,0,0,0.15)",
          cursor: "pointer",
          verticalAlign: "middle",
        }}
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Cohort color"
        style={{
          position: "absolute", inset: 0, opacity: 0, cursor: "pointer", border: "none",
        }}
      >
        {COHORT_COLORS.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    </span>
  );
}

function DateRangeChip({
  from, to, onChange, onClear,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  onClear: () => void;
}) {
  const display = from && to
    ? (from === to ? from : `${from} → ${to}`)
    : (from || to || "All time");
  return (
    <PopoverChip label="Cohort" display={display} width={260}>
      {() => (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--fg-3)", width: 36 }}>From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => onChange(e.target.value, to || e.target.value)}
              style={{ flex: 1, padding: "3px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 4 }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "var(--fg-3)", width: 36 }}>To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => onChange(from || e.target.value, e.target.value)}
              style={{ flex: 1, padding: "3px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 4 }}
            />
          </label>
          {(from || to) && (
            <button
              type="button"
              onClick={onClear}
              style={{
                fontSize: 11, padding: "3px 6px", border: "1px solid var(--border)", borderRadius: 4,
                background: "transparent", color: FG1, cursor: "pointer",
              }}
            >
              Clear (all time)
            </button>
          )}
        </div>
      )}
    </PopoverChip>
  );
}

function PopoverChip({
  label,
  display,
  children,
  width,
}: {
  label: string;
  display: string;
  children: (close: () => void) => React.ReactNode;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);
  // Wrapper is NOT .filter-chip so that global `.filter-chip select` rules
  // don't bleed into popover-rendered selects/inputs.
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <span className="filter-chip" style={{ position: "relative" }}>
        <span className="chip-label">{label}:</span>
        <span className="chip-value">{display}</span>
        <i className="ti ti-chevron-down chevron" />
        <button
          type="button"
          className="chip-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`${label}: ${display}`}
        />
      </span>
      {open && (
        <div
          className="month-multi-popover"
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          style={{ minWidth: width ?? 240, padding: 8 }}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </span>
  );
}

function TriStateChip({
  label, value, yesLabel, noLabel, onChange,
}: {
  label: string;
  value: "any" | "yes" | "no";
  yesLabel: string;
  noLabel: string;
  onChange: (v: "any" | "yes" | "no") => void;
}) {
  const display = value === "any" ? `Any` : value === "yes" ? yesLabel : noLabel;
  return (
    <PopoverChip label={label} display={display} width={180}>
      {(close) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {([
            { v: "any" as const, label: "Any" },
            { v: "yes" as const, label: yesLabel },
            { v: "no" as const, label: noLabel },
          ]).map((opt) => (
            <label key={opt.v} className="row" style={{ cursor: "pointer" }}>
              <input
                type="radio"
                checked={value === opt.v}
                onChange={() => { onChange(opt.v); close(); }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </PopoverChip>
  );
}

function ReachedStageChip({
  value, onChange,
}: {
  value: StageKey | "any";
  onChange: (v: StageKey | "any") => void;
}) {
  const display = value === "any" ? "Any" : (STAGES.find((s) => s.key === value)?.short ?? value);
  return (
    <PopoverChip label="Reached" display={display} width={220}>
      {(close) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label className="row" style={{ cursor: "pointer" }}>
            <input
              type="radio"
              checked={value === "any"}
              onChange={() => { onChange("any"); close(); }}
            />
            <span>Any (no constraint)</span>
          </label>
          <div className="divider" />
          {STAGES.map((s) => (
            <label key={s.key} className="row" style={{ cursor: "pointer" }}>
              <input
                type="radio"
                checked={value === s.key}
                onChange={() => { onChange(s.key); close(); }}
              />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
      )}
    </PopoverChip>
  );
}

function ExcludePaidChip({
  value, onChange,
}: {
  value: { enabled: boolean; hours: number };
  onChange: (v: { enabled: boolean; hours: number }) => void;
}) {
  const display = value.enabled ? `Excl. paid <${value.hours}h` : "Include all";
  return (
    <PopoverChip label="Guard" display={display} width={260}>
      {() => (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label className="row" style={{ cursor: "pointer" }}>
            <input
              type="radio"
              checked={!value.enabled}
              onChange={() => onChange({ ...value, enabled: false })}
            />
            <span>Include all users</span>
          </label>
          <label className="row" style={{ cursor: "pointer" }}>
            <input
              type="radio"
              checked={value.enabled}
              onChange={() => onChange({ ...value, enabled: true })}
            />
            <span>Exclude users on paid plan within…</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: 22, opacity: value.enabled ? 1 : 0.5 }}>
            <input
              type="number"
              min={0}
              value={value.hours}
              disabled={!value.enabled}
              onChange={(e) => onChange({ ...value, hours: Math.max(0, Number(e.target.value) || 0) })}
              style={{ width: 70, padding: "3px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 4 }}
            />
            <span style={{ fontSize: 12, color: "var(--fg-3)" }}>hours of signup</span>
          </div>
        </div>
      )}
    </PopoverChip>
  );
}

const OP_LABEL: Record<DayOp, string> = {
  within: "within",
  after: "after",
  never: "never",
};

function timingShortLabel(t: StageTiming): string {
  const stage = STAGES.find((s) => s.key === t.stage);
  const stageLbl = stage?.short ?? t.stage;
  if (t.op === "never") return `${stageLbl}: never`;
  return `${stageLbl}: ${t.op} ${t.days}d`;
}

function StageTimingsControl({
  timings,
  onChange,
}: {
  timings: StageTiming[];
  onChange: (next: StageTiming[]) => void;
}) {
  const updateAt = (i: number, patch: Partial<StageTiming>) => {
    onChange(timings.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  };
  const removeAt = (i: number) => onChange(timings.filter((_, idx) => idx !== i));
  const add = () => {
    onChange([...timings, { id: newTimingId(), stage: "calendar", op: "within", days: 1 }]);
  };

  return (
    <>
      {timings.map((t, i) => (
        <TimingChip
          key={t.id}
          timing={t}
          onChange={(patch) => updateAt(i, patch)}
          onRemove={() => removeAt(i)}
        />
      ))}
      <button
        type="button"
        onClick={add}
        title="Add a stage-timing constraint"
        style={{
          fontSize: 11,
          padding: "3px 8px",
          border: "1px dashed var(--border)",
          borderRadius: 6,
          background: "transparent",
          color: FG1,
          cursor: "pointer",
        }}
      >
        + Stage timing
      </button>
    </>
  );
}

function TimingChip({
  timing,
  onChange,
  onRemove,
}: {
  timing: StageTiming;
  onChange: (patch: Partial<StageTiming>) => void;
  onRemove: () => void;
}) {
  return (
    <PopoverChip label="When" display={timingShortLabel(timing)} width={300}>
      {() => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>Stage</div>
            <select
              value={timing.stage}
              onChange={(e) => onChange({ stage: e.target.value as StageKey })}
              style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 4 }}
            >
              {STAGES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>Constraint</div>
            <div style={{ display: "flex", gap: 4 }}>
              {(["within", "after", "never"] as const).map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => onChange({ op })}
                  style={{
                    flex: 1,
                    padding: "4px 6px",
                    fontSize: 11,
                    border: "1px solid var(--border)",
                    borderRadius: 4,
                    background: timing.op === op ? PRIMARY : "transparent",
                    color: timing.op === op ? "#fff" : FG1,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {OP_LABEL[op]}
                </button>
              ))}
            </div>
          </div>
          {timing.op !== "never" && (
            <div>
              <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>Days since signup</div>
              <input
                type="number"
                min={0}
                value={timing.days}
                onChange={(e) => onChange({ days: Math.max(0, Number(e.target.value) || 0) })}
                style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 4 }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={onRemove}
            style={{
              fontSize: 11,
              padding: "4px 6px",
              border: "1px solid var(--border)",
              borderRadius: 4,
              background: "transparent",
              color: "var(--fg-danger, #B45A3F)",
              cursor: "pointer",
            }}
          >
            Remove constraint
          </button>
        </div>
      )}
    </PopoverChip>
  );
}

function HistogramSection({
  cohortUsers,
  granularity,
  onGranularityChange,
  observationDays,
  onObservationDaysChange,
  selectedStages,
  onSelectedStagesChange,
  stageOptions,
}: {
  cohortUsers: { cohort: CohortDef; users: UserRowV2[] }[];
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  observationDays: number;
  onObservationDaysChange: (d: number) => void;
  selectedStages: StageKey[];
  onSelectedStagesChange: (next: StageKey[]) => void;
  stageOptions: { value: string; label: string }[];
}) {
  const selectedSet = useMemo(() => new Set(selectedStages), [selectedStages]);
  const hourlyAllowed = observationDays <= HOURLY_MAX_DAYS;
  const fifteenAllowed = observationDays <= FIFTEEN_MIN_MAX_DAYS;

  // Per-cohort histograms keyed by stage.
  const perCohort = useMemo(() => {
    return cohortUsers.map(({ cohort, users }) => ({
      cohort,
      users,
      histograms: buildHistograms(users, granularity, observationDays, selectedSet),
    }));
  }, [cohortUsers, granularity, observationDays, selectedSet]);

  const stagesShown = STAGES.filter((s) => selectedSet.has(s.key));

  const tileHeight = stagesShown.length <= 1 ? 240
    : stagesShown.length <= 2 ? 180
    : stagesShown.length <= 4 ? 140
    : 110;

  const granularityNoun: Record<Granularity, string> = {
    "15min": "15-minute interval",
    hour: "hour",
    day: "day",
    week: "week",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>
          Each tile shows one milestone; bars show how many users in each cohort first hit it in that {granularityNoun[granularity]} since sign-up.
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <MultiChip
            label="Events"
            allLabel="All events"
            pluralNoun="events"
            options={stageOptions}
            selected={selectedStages.length === STAGES.length ? [] : selectedStages}
            onChange={(next) => onSelectedStagesChange(next.length === 0 ? STAGES.map((s) => s.key) : (next as StageKey[]))}
          />
          <div role="group" aria-label="Bucket size" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
            {(["15min", "hour", "day", "week"] as const).map((g) => {
              const active = granularity === g;
              const disabled = (g === "hour" && !hourlyAllowed) || (g === "15min" && !fifteenAllowed);
              const label = g === "15min" ? "15 min" : g === "hour" ? "Hourly" : g === "day" ? "Daily" : "Weekly";
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => { if (!disabled) onGranularityChange(g); }}
                  style={{
                    padding: "3px 10px",
                    background: active ? PRIMARY : "transparent",
                    color: active ? "#fff" : disabled ? "var(--fg-muted)" : FG1,
                    border: "none",
                    cursor: disabled ? "not-allowed" : "pointer",
                    fontWeight: active ? 600 : 400,
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <span className="filter-chip" style={{ position: "relative" }}>
            <span className="chip-label">Window:</span>
            <span className="chip-value">{observationDays} day{observationDays === 1 ? "" : "s"}</span>
            <i className="ti ti-chevron-down chevron" />
            <select
              aria-label="Observation window"
              value={observationDays}
              onChange={(e) => onObservationDaysChange(Number(e.target.value))}
              style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", border: "none", background: "transparent" }}
            >
              {[1, 7, 30, 90, 180, 365].map((opt) => (
                <option key={opt} value={opt}>{opt} day{opt === 1 ? "" : "s"}</option>
              ))}
            </select>
          </span>
        </div>
      </div>
      {stagesShown.length === 0 ? (
        <div className="empty">No events selected. Use the Events picker above to choose at least one milestone.</div>
      ) : (
        stagesShown.map((stage) => (
          <OverlayHistogramTile
            key={stage.key}
            stageLabel={stage.label}
            stageKey={stage.key}
            cohortHistograms={perCohort.map(({ cohort, histograms }) => {
              const h = histograms.find((x) => x.stage.key === stage.key);
              return { cohort, hist: h };
            })}
            height={tileHeight}
            observationDays={observationDays}
          />
        ))
      )}
    </div>
  );
}

function OverlayHistogramTile({
  stageLabel,
  stageKey,
  cohortHistograms,
  height,
  observationDays,
}: {
  stageLabel: string;
  stageKey: StageKey;
  cohortHistograms: { cohort: CohortDef; hist: ReturnType<typeof buildHistograms>[number] | undefined }[];
  height: number;
  observationDays: number;
}) {
  // Build merged data: one row per bucket index, with cohort.id keys.
  const data = useMemo(() => {
    const first = cohortHistograms.find((c) => c.hist != null)?.hist;
    if (!first) return [];
    return first.buckets.map((b, i) => {
      const row: Record<string, number | string> = { label: b.label };
      for (const ch of cohortHistograms) {
        if (!ch.hist) continue;
        row[ch.cohort.id] = ch.hist.buckets[i]?.count ?? 0;
      }
      return row;
    });
  }, [cohortHistograms]);

  const xInterval = Math.max(0, Math.floor(data.length / 12));

  return (
    <div className="chart-tile" style={{ paddingBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: FG1, margin: 0 }}>{stageLabel}</h3>
        <div style={{ fontSize: 11, color: "var(--fg-3)", display: "flex", gap: 12, flexWrap: "wrap" }}>
          {cohortHistograms.map(({ cohort, hist }) => {
            const reached = hist?.reachedCount ?? 0;
            const total = hist?.cohortTotal ?? 0;
            const pct = total === 0 ? 0 : (reached / total) * 100;
            return (
              <span key={cohort.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: cohort.color, display: "inline-block" }} />
                <strong style={{ color: FG1 }}>{cohort.label}:</strong>
                {fmtN(reached)}/{fmtN(total)} ({pct.toFixed(1)}%)
              </span>
            );
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 16 }} barCategoryGap={1}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: MUTED }}
            interval={xInterval}
            tickLine={false}
            axisLine={{ stroke: "rgba(0,0,0,0.15)" }}
            height={16}
          />
          <YAxis tick={{ fontSize: 9, fill: MUTED }} width={32} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.04)" }}
            formatter={(v: unknown, _n, item) => {
              const id = String(item?.dataKey ?? "");
              const ch = cohortHistograms.find((c) => c.cohort.id === id);
              const total = ch?.hist?.cohortTotal ?? 0;
              const count = Number(v) || 0;
              const pct = total === 0 ? 0 : (count / total) * 100;
              return [`${fmtN(count)} · ${pct.toFixed(2)}% of cohort`, ch?.cohort.label ?? id];
            }}
          />
          {cohortHistograms.length > 1 && (
            <Legend
              wrapperStyle={{ fontSize: 10 }}
              formatter={(value: unknown) => {
                const id = String(value);
                return cohortHistograms.find((c) => c.cohort.id === id)?.cohort.label ?? id;
              }}
            />
          )}
          {cohortHistograms.map(({ cohort }) => (
            <Bar
              key={cohort.id}
              dataKey={cohort.id}
              fill={cohort.color}
              isAnimationActive={false}
              name={cohort.id}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <span style={{ fontSize: 10, color: "var(--fg-muted)" }}>
        Window: first {observationDays}d after sign-up · stage: {stageKey}
      </span>
    </div>
  );
}

function KpiTableSection({
  cohortUsers,
}: {
  cohortUsers: { cohort: CohortDef; users: UserRowV2[] }[];
}) {
  const measures = cohortUsers.map(({ cohort, users }) => ({ cohort, m: computeMeasures(users) }));
  const fmtMed = (n: number | null | undefined) => (n == null ? "—" : `${n}d`);
  const cellStyle: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid var(--border)", fontSize: 12, textAlign: "right" };
  const labelStyle: React.CSSProperties = { ...cellStyle, textAlign: "left", color: "var(--fg-2)", fontWeight: 500 };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...labelStyle, fontWeight: 600 }}>Measure</th>
            {measures.map(({ cohort }) => (
              <th key={cohort.id} style={{ ...cellStyle, color: cohort.color, fontWeight: 600 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: cohort.color }} />
                  {cohort.label}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={labelStyle}>Cohort size</td>
            {measures.map(({ cohort, m }) => <td key={cohort.id} style={cellStyle}>{fmtN(m.size)}</td>)}
          </tr>
          <tr>
            <td style={labelStyle}>Conversion to paid</td>
            {measures.map(({ cohort, m }) => (
              <td key={cohort.id} style={cellStyle}>{m.paidConversionPct.toFixed(1)}%</td>
            ))}
          </tr>
          <tr>
            <td style={labelStyle}>Median days to paid</td>
            {measures.map(({ cohort, m }) => <td key={cohort.id} style={cellStyle}>{fmtMed(m.medianDaysToPaid)}</td>)}
          </tr>
          {STAGES.filter((s) => s.key !== "paid").map((stage) => (
            <tr key={stage.key}>
              <td style={labelStyle}>Reached: {stage.label}</td>
              {measures.map(({ cohort, m }) => {
                const n = m.reached[stage.key] ?? 0;
                const med = m.medianDays[stage.key];
                return (
                  <td key={cohort.id} style={cellStyle}>
                    {fmtPct(n, m.size)} <span style={{ color: "var(--fg-3)" }}>({fmtN(n)}{med != null ? ` · ${med}d` : ""})</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helpful re-export so consumers can use it without importing the helper directly.
export type { CohortDef };

// Suppress unused warnings for groupCountry import (used implicitly via filters).
void groupCountry;
